import { ReactNode } from 'react';
import { FieldContext, RequiredMark, useFieldSetup } from './FieldContext';
import { HintOverlay } from './HintOverlay';

type Props = {
  label: ReactNode;
  hint?: ReactNode;
  // Small control rendered at the right edge of the label row (same line as
  // the title). Intended for compact affordances like a drill-in icon — use
  // `hint` for text explanations that belong below the field.
  labelAction?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
  // Inline-label layout: render the label on the SAME line as the control,
  // left-aligned. Must be used inside a <FieldGrid> (a shared
  // grid-cols-[max-content_1fr] grid) so every row's label column lines up —
  // this mirrors the AR-invoice header's [label | value] layout. The default
  // (false) keeps the classic stacked label-above-field layout.
  row?: boolean;
  // In a multi-pair FieldGrid (cols=2), make this row field span the WHOLE row
  // instead of one label|control pair. Use for wide selects / readouts.
  full?: boolean;
  // Stacked only: wrap in a <div> instead of a <label>, for a field holding
  // several controls (a multiselect's options) whose title must not activate
  // the first of them. The title keeps its id; the controls name themselves
  // through the FieldContext label id.
  group?: boolean;
};

// The hint shows on hover or focus (HintOverlay); errors still render inline
// (they need to be seen without hovering).
export function FormField({ label, hint, labelAction, error, required, children, className = '', row = false, full = false, group = false }: Props) {
  // A real <label>: the control is named by it (id + htmlFor, and
  // aria-labelledby through the FieldContext for buttons such as Select,
  // AsyncPicker and DatePicker).
  const { info, labelId, labelFor, content, errorId, hintId } = useFieldSetup({ children, required, error, hint });
  // Always in the page (sr-only), so the control's aria-describedby resolves
  // without hovering. Kept outside the stacked <label> so it never joins a name.
  const hintText = hint ? <span id={hintId} className="sr-only">{hint}</span> : null;
  if (row) {
    // label | control cells. By default the field spans one [max-content 1fr]
    // PAIR (col-span-2) and uses subgrid so its two cells land on FieldGrid's
    // tracks (keeping label columns aligned across rows). When `full`, it spans
    // the whole row and defines its own [label | control] tracks so the control
    // stretches edge to edge. The zoom-picker (labelAction) sits at the right
    // edge of the control cell, like the invoice's zoom-gutter.
    const wrap = full
      ? `grid grid-cols-[max-content_1fr] col-span-full items-center gap-x-3 ${className}`
      : `grid grid-cols-subgrid col-span-2 items-center gap-x-3 ${className}`;
    // Label + value styled like the AR-invoice header (<dt>/<dd>): muted,
    // non-uppercase, text-sm — so inline-label forms read identically to the
    // invoice header.
    return (
      <div className={`${wrap} text-sm`}>
        <label id={labelId} htmlFor={labelFor} className="field-label whitespace-nowrap py-0.5">
          {label}
          {required && <RequiredMark />}
        </label>
        <div className="group relative min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="flex-1 min-w-0"><FieldContext.Provider value={info}>{content}</FieldContext.Provider></div>
            {labelAction && <div className="shrink-0">{labelAction}</div>}
          </div>
          {hintText}
          {hint && !error && <HintOverlay hint={hint} />}
          {error && <div id={errorId} role="alert" className="text-meta text-danger mt-0.5">{error}</div>}
        </div>
      </div>
    );
  }
  // Stacked: the wrapping <label> keeps naming whatever it wraps (no htmlFor,
  // so a child that does not read the context is still labelled implicitly);
  // context-aware controls are named by the title text through labelId.
  const Wrap = group ? 'div' : 'label';
  return (
    <>
    <Wrap className={`group relative block ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <div id={labelId} className="text-label text-secondary uppercase tracking-[0.04em]">
          {label}
          {required && <RequiredMark />}
        </div>
        {labelAction && <div className="shrink-0">{labelAction}</div>}
      </div>
      <FieldContext.Provider value={info}>{content}</FieldContext.Provider>
      {hint && !error && <HintOverlay hint={hint} />}
      {error && <div id={errorId} role="alert" className="text-meta text-danger mt-1">{error}</div>}
    </Wrap>
    {hintText}
    </>
  );
}

// Container for inline-label (row) FormFields: a [label | control] grid so
// every row's label column shares one width and the controls line up. Drop your
// <FormField row ...> children inside. New forms should prefer this inline-label
// layout for header/detail field blocks (matches the AR invoice).
//
// `cols` = how many label|control PAIRS sit side by side per row (default 1).
// With cols=2 the grid has two [max-content 1fr] pairs, so fields lay out two
// per row; a field can still span the full width with `col-span-full`.
export function FieldGrid({ children, cols = 1, className = '' }: { children: ReactNode; cols?: 1 | 2; className?: string }) {
  const tracks = cols === 2
    ? 'md:grid-cols-[max-content_1fr_max-content_1fr]'
    : '';
  return (
    <div className={`grid grid-cols-[max-content_1fr] ${tracks} gap-x-3 gap-y-1 ${className}`}>
      {children}
    </div>
  );
}

// Stacked-field grid (layout-03): one column on a phone, two from 640px, the
// asked-for count from 1024px. Use formGridCols(n) in a className, or
// <FormGrid cols={n}>; never a bare grid-cols-N (the guard test rejects it).
const FORM_GRID: Record<2 | 3 | 4 | 5, string> = {
  2: 'grid grid-cols-1 sm:grid-cols-2',
  3: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  5: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
};
export function formGridCols(cols: 2 | 3 | 4 | 5): string { return FORM_GRID[cols]; }
export function FormGrid({ cols = 2, className = 'gap-3', children }: { cols?: 2 | 3 | 4 | 5; className?: string; children: ReactNode }) {
  return <div className={`${FORM_GRID[cols]} ${className}`}>{children}</div>;
}
